import type { Square } from '../game/types';

export interface Point {
  x: number;
  y: number;
}

export type PiecePointerSnapshot =
  | { phase: 'idle' }
  | { phase: 'selected'; square: Square }
  | { phase: 'dragging'; square: Square; x: number; y: number };

interface ActiveInteraction {
  square: Square;
  pressedCss: Point;
  grabOffset: Point;
  phase: 'selected' | 'dragging';
  x: number;
  y: number;
}

export class PieceInteractionState {
  private active: ActiveInteraction | null = null;

  constructor(private readonly thresholdCssPx = 6) {}

  press(input: {
    square: Square;
    pointerCss: Point;
    pointerCanvas: Point;
    pieceCenter: Point;
  }): void {
    this.active = {
      square: input.square,
      pressedCss: input.pointerCss,
      grabOffset: {
        x: input.pieceCenter.x - input.pointerCanvas.x,
        y: input.pieceCenter.y - input.pointerCanvas.y,
      },
      phase: 'selected',
      x: input.pieceCenter.x,
      y: input.pieceCenter.y,
    };
  }

  move(pointerCss: Point, pointerCanvas: Point): PiecePointerSnapshot {
    if (!this.active) return { phase: 'idle' };
    const distance = Math.hypot(
      pointerCss.x - this.active.pressedCss.x,
      pointerCss.y - this.active.pressedCss.y,
    );
    if (this.active.phase === 'selected' && distance < this.thresholdCssPx) {
      return this.snapshot();
    }
    this.active.phase = 'dragging';
    this.active.x = pointerCanvas.x + this.active.grabOffset.x;
    this.active.y = pointerCanvas.y + this.active.grabOffset.y;
    return this.snapshot();
  }

  release(): PiecePointerSnapshot {
    const released = this.snapshot();
    this.active = null;
    return released;
  }

  cancel(): void {
    this.active = null;
  }

  snapshot(): PiecePointerSnapshot {
    if (!this.active) return { phase: 'idle' };
    if (this.active.phase === 'selected') {
      return { phase: 'selected', square: this.active.square };
    }
    return {
      phase: 'dragging',
      square: this.active.square,
      x: this.active.x,
      y: this.active.y,
    };
  }
}
